import React, { useState, useEffect, useMemo } from 'react';
import { FaUsers, FaSearch, FaEdit, FaTrash, FaPlus, FaFilter, FaStore, FaCoins } from 'react-icons/fa';
import { AdminAffiliateApiService } from '../../../service/admin/adminAffiliateApiService';
import ExcelTable from '../../../component/library/excelTable';
import './AffiliateManagement.css';

export default function AffiliateManagement() {
  const [affiliates, setAffiliates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [commissionPlans, setCommissionPlans] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [analytics, setAnalytics] = useState({
    totalAffiliates: 0,
    activeAffiliates: 0,
    inactiveAffiliates: 0,
    suspendedAffiliates: 0,
    totalEarnings: 0,
    totalSellers: 0,
    monthlyGrowth: 0
  });

  useEffect(() => {
    fetchAffiliates();
    fetchCommissionPlans();
  }, []);

  useEffect(() => {
    fetchAffiliates();
  }, [page, limit, searchTerm]);

const fetchAffiliates = async () => {
  try {
    setLoading(true);
    const params = { page, limit };
    if (searchTerm) params.search = searchTerm;
    
    const response = await AdminAffiliateApiService.getAffiliates(params);

    // API returns: { affiliates: [], pagination: {} }
    const list = response?.data?.affiliates || [];
    setAffiliates(Array.isArray(list) ? list : []);
    setPagination(response?.data?.pagination || { currentPage: 1, totalPages: 1, totalItems: 0 });
    
    // Calculate analytics
    if (Array.isArray(list)) {
      const totalAffiliates = list.length;
      const activeAffiliates = list.filter(a => a.status === 'Active').length;
      const inactiveAffiliates = list.filter(a => a.status === 'Inactive').length;
      const suspendedAffiliates = list.filter(a => a.status === 'Suspended').length;
      const totalEarnings = list.reduce((sum, a) => sum + (a.totalEarnings || 0), 0);
      const totalSellers = list.reduce((sum, a) => sum + (a.sellersCount || 0), 0);
      
      setAnalytics({
        totalAffiliates,
        activeAffiliates,
        inactiveAffiliates,
        suspendedAffiliates,
        totalEarnings,
        totalSellers,
        monthlyGrowth: 0 // TODO: Calculate from historical data
      });
    }
  } catch (error) {
    console.error("Error fetching affiliates:", error);
    setAffiliates([]); // safety
  } finally {
    setLoading(false);
  }
};

const fetchCommissionPlans = async () => {
  try {
    const response = await AdminAffiliateApiService.getCommissionPlansForDropdown();
    const list = response?.data || [];
    setCommissionPlans(Array.isArray(list) ? list : []);
  } catch (error) {
    console.error("Error fetching commission plans:", error);
    setCommissionPlans([]);
  }
};

const handleCommissionPlanChange = async (affiliateId, commissionPlanId) => {
  try {
    await AdminAffiliateApiService.updateAffiliate(affiliateId, {
      commissionPlan: commissionPlanId || null
    });
    fetchAffiliates();
  } catch (error) {
    console.error("Error updating commission plan:", error);
  }
};

const handleStatusChange = async (affiliateId, newStatus) => {
  try {
    await AdminAffiliateApiService.updateAffiliateStatus(affiliateId, newStatus);
    fetchAffiliates();
  } catch (error) {
    console.error("Error updating affiliate status:", error);
  }
};

const handleDeleteAffiliate = async (affiliateId) => {
  if (window.confirm('Are you sure you want to delete this affiliate?')) {
    try {
      await AdminAffiliateApiService.deleteAffiliate(affiliateId);
      fetchAffiliates();
    } catch (error) {
      console.error("Error deleting affiliate:", error);
    }
  }
};

  const tableData = useMemo(
    () => (affiliates || []),
    [affiliates]
  );

  const columns = [
    {
      key: "name",
      label: "Name",
      width: 200,
      render: (_, row) => {
        const name = `${row?.firstName || ''} ${row?.lastName || ''}`.trim() || '-';
        return <span className="affiliate-mgmt-name-cell">{name}</span>;
      },
    },
    {
      key: "email",
      label: "Email",
      width: 220,
      render: (value) => <span className="affiliate-mgmt-email-cell">{value || "-"}</span>,
    },
    {
      key: "affiliateReferralCode",
      label: "Code",
      width: 150,
      render: (value) => (
        <span className="affiliate-code">{value || '-'}</span>
      ),
    },
    {
      key: "sellersCount",
      label: "Sellers",
      width: 100,
      render: (value) => Number(value || 0).toLocaleString(),
    },
    {
      key: "totalEarnings",
      label: "Earnings",
      width: 120,
      render: (value) => `₹${Number(value || 0).toLocaleString()}`,
    },
    {
      key: "commissionPlan",
      label: "Commission Plan",
      width: 200,
      render: (_, row) => (
        <select 
          className="affiliate-mgmt-commission-plan-select"
          value={row?.commissionPlan?.id || ''}
          onChange={(e) => handleCommissionPlanChange(row._id, e.target.value || null)}
        >
          <option value="">Select Plan</option>
          {commissionPlans.map(plan => (
            <option key={plan._id} value={plan._id}>
              {plan.name} ({plan.commissionRate}%)
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "status",
      label: "Status",
      width: 120,
      render: (_, row) => (
        <select 
          className={`status-select ${(row?.status || 'Active').toLowerCase()}`}
          value={row?.status || 'Active'}
          onChange={(e) => handleStatusChange(row._id, e.target.value)}
        >
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="Suspended">Suspended</option>
        </select>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      width: 80,
      render: (_, row) => (
        <div className="affiliate-mgmt-action-buttons">
          <button 
            className="affiliate-mgmt-btn-icon affiliate-mgmt-btn-delete"
            onClick={() => handleDeleteAffiliate(row._id)}
            title="Delete Affiliate"
          >
            <FaTrash />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="affiliate-mgmt-page">
      <div className="affiliate-mgmt-header">
        <h1>Affiliate Management</h1>
        <p>Manage and monitor all affiliate partners</p>
      </div>

      <div className="admin-stats-cards">
        <div className="stat-card">
          <div className="am-stat-icon">
            <FaUsers />
          </div>
          <div className="stat-content">
            <h3>{analytics.totalAffiliates.toLocaleString()}</h3>
            <p>Total Affiliates</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="am-stat-icon">
            <FaStore />
          </div>
          <div className="am-stat-content">
            <h3>{analytics.totalSellers.toLocaleString()}</h3>
            <p>Total Sellers</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="am-stat-icon">
            <FaCoins />
          </div>
          <div className="am-stat-content">
            <h3>₹{analytics.totalEarnings.toLocaleString()}</h3>
            <p>Total Earnings</p>
          </div>
        </div>
      </div>

      <ExcelTable
        isLoading={loading}
        loadingText="Loading affiliates..."
        data={tableData}
        columns={columns}
        showToolbarExport={true}
        showToolbarFilter={false}
        showToolbarColumns={true}
        showToolbarFullscreen={true}
        showToolbar={true}
        title="Affiliates"
        pagination={pagination}
        onPageChange={setPage}
        onLimitChange={setLimit}
        searchable={true}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        selectable={false}
        sortable={true}
        resizable={true}
        showRefresh={true}
        onRefresh={fetchAffiliates}
        showRowsPerPage={true}
      />
    </div>
  );
}
